import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import DOMPurify from 'dompurify';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  IconButton,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import CloseIcon from '@mui/icons-material/Close';
import WYSIWYGEditor from 'components/WYSIWYGEditor/WYSIWYGEditor';

import {
  getPolicyTypes,
  getPolicyById,
  resetPolicyDetails,
} from 'store/reducers/legalSlice';

const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
    },
  },
};

const AddPolicy = ({
  openModal,
  onCloseModal,
  isEdit,
  onCreatePolicy,
  onUpdatePolicy,
  selectedPolicy,
}) => {
  const dispatch = useDispatch();
  const { policyTypesList, policyDetails } = useSelector(
    (state) => state.legal
  );

  const [isLoading, setLoading] = useState(false);
  const [isFetchingPolicy, setFetchingPolicy] = useState(false);

  const policySchema = yup.object({
    title: yup.string().required(`Policy title is required`),
    content: yup
      .string()
      .required(`Policy content is required`)
      .test('test-content', 'Policy Content is required', (value, ctx) => {
        if (
          value === '<p></p>' ||
          value === '<p>&nbsp;</p>' ||
          value === '<p></p>\n' ||
          value === '<p>&nbsp;</p>\n'
        ) {
          return ctx.createError({
            path: 'content',
            message: 'Policy Content is required',
          });
        }
        return true;
      }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting, dirtyFields },
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(policySchema),
  });

  async function fetchPolicyTypes() {
    try {
      await dispatch(getPolicyTypes());
    } catch (error) {
      console.log({ error });
    }
  }

  async function fetchPolicy() {
    try {
      setFetchingPolicy(true);
      await dispatch(getPolicyById(selectedPolicy?.id));
    } catch (error) {
      console.log({ error });
      onCloseModal();
    } finally {
      setFetchingPolicy(false);
    }
  }

  useEffect(() => {
    if (isEdit && selectedPolicy?.id) fetchPolicy();
  }, [isEdit, selectedPolicy]);

  useEffect(() => {
    if (policyDetails) {
      reset(
        {
          title: policyDetails?.title,
          content: policyDetails?.content,
        },
        {
          keepIsValid: false,
          keepErrors: true,
          keepDirty: false,
        }
      );
    }
  }, [policyDetails]);

  useEffect(() => {
    if (!policyTypesList?.length) fetchPolicyTypes();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetPolicyDetails());
    };
  }, []);

  const onAddPolicySubmit = (data) => {
    try {
      setLoading(true);
      const cleanHtml = DOMPurify.sanitize(data?.content);
      onCreatePolicy({ title: data?.title, content: cleanHtml });
    } catch (error) {
      console.log({ error });
      toast.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  const onUpdatePolicySubmit = (data) => {
    try {
      setLoading(true);
      const cleanHtml = DOMPurify.sanitize(data?.content);
      onUpdatePolicy({
        legalId: selectedPolicy?.id?.toString(),
        title: data?.title,
        content: cleanHtml,
        isActive: true,
      });
    } catch (error) {
      console.log({ error });
      toast.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onCloseModal();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableEscapeKeyDown
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          maxHeight: 'calc(100vh - 100px)',
          width: '700px',
          maxWidth: 'calc(100vw - 48px)',
          overflow: 'auto',
          p: 4,
          borderRadius: 3,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDTypography id="modal-modal-title" variant="h4" component="h2">
            {isEdit ? 'Edit Policy' : 'Add Policy'}
          </MDTypography>
          <IconButton onClick={onCloseModal}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </MDBox>
        {isFetchingPolicy ? (
          <Box
            sx={{
              textAlign: 'center',
              paddingTop: '32px',
              paddingBottom: '24px',
              height: '800px',
              display: 'grid',
              placeItems: 'center',
              maxHeight: 'calc(100vh - 100px)',
            }}
          >
            <CircularProgress color="info" />
          </Box>
        ) : (
          <>
            <MDBox marginTop="32px">
              <MDTypography variant="h5" mb={2}>
                Policy Title
              </MDTypography>

              <FormControl
                sx={{ minHeight: 48 }}
                fullWidth
                error={!!errors?.title}
              >
                <InputLabel id="policy-title">Select Type</InputLabel>
                <Controller
                  name="title"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      sx={{ minHeight: 48 }}
                      input={<OutlinedInput label="Select Type" />}
                      MenuProps={MenuProps}
                      autoFocus
                    >
                      {policyTypesList?.map((policyType) => (
                        <MenuItem value={policyType} key={policyType}>
                          {policyType}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  defaultValue=""
                />
                {errors?.title && (
                  <FormHelperText>{errors?.title?.message}</FormHelperText>
                )}
              </FormControl>
            </MDBox>
            <MDBox marginTop="32px">
              <MDTypography variant="h5" mb={2}>
                Policy Content
              </MDTypography>
              <Controller
                name="content"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <WYSIWYGEditor value={value} onChange={onChange} />
                )}
              />
              {errors?.content && (
                <MDTypography color="error" variant="caption">
                  {errors?.content?.message}
                </MDTypography>
              )}
            </MDBox>
            <MDBox align="right" marginTop="32px">
              <MDButton onClick={onCloseModal}>Discard</MDButton>
              {isEdit ? (
                <>
                  <MDButton
                    onClick={handleSubmit(onUpdatePolicySubmit)}
                    color="info"
                    disabled={
                      Object.keys(errors).length > 0 ||
                      !Object.keys(dirtyFields).length ||
                      !isValid ||
                      isSubmitting
                    }
                  >
                    Save
                  </MDButton>
                </>
              ) : (
                <MDButton
                  onClick={handleSubmit(onAddPolicySubmit)}
                  color="info"
                  disabled={
                    Object.keys(errors).length > 0 || !isValid || isSubmitting
                  }
                  isLoading={isLoading}
                >
                  Create
                </MDButton>
              )}
            </MDBox>
          </>
        )}
      </Box>
    </Modal>
  );
};

AddPolicy.propTypes = {
  openModal: PropTypes.bool,
  onCloseModal: PropTypes.func,
  isEdit: PropTypes.bool,
  onCreatePolicy: PropTypes.func,
  onUpdatePolicy: PropTypes.func,
  selectedPolicy: PropTypes.object,
};

AddPolicy.defaultProps = {
  openModal: false,
  onCloseModal: () => {},
  isEdit: false,
  onCreatePolicy: () => {},
  onUpdatePolicy: () => {},
  selectedPolicy: null,
};

export default AddPolicy;
